/* eslint-disable */
// @ts-nocheck

import { Dispatch, SetStateAction, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'react-toastify'

import {
  PencilSquareIcon,
  PlusIcon,
  TrashIcon,
} from '@heroicons/react/24/outline'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import {
  deleteContainerCosts,
  getContainerCosts,
  patchContainerCosts,
  postContainerCosts,
} from '@/api/containersAPI'
import { getCostCategories } from '@/api/costCategoriesAPI'
import { getCurrencies } from '@/api/currenciesAPI'
import { getErrorMessage } from '@/utils/errorHandlers'
import Button from '@/components/misc/Button'
import ButtonLoading from '@/components/misc/Button'
import SpinnerLoading from '@/components/misc/SpinnerLoading'
import {
  ButtonPrimary,
  DataTable,
  Modal,
  SelectForm,
  TableContextProvider,
  TextAreaForm,
} from '@/components/new'
import { InputCurrencyForm } from '@/components/new/form/InputCurrencyForm'
import { InputForm } from '@/components/new/form/InputForm'
import { useModalWizardStepsContext } from '@/components/new/modalWizardSteps/ModalWizardStepsContext'
import { useModalWizardTabsContext } from '@/components/new/modalWizardTabs/ModalWizardTabsContext'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button as ButtonCancelar } from '@/components/ui/button'
import { Form } from '@/components/ui/form'
import { ICost } from '@/containers/logistic/expedients/wizzardExpedient/tabs/cost/interfaces'
import { SelectInterface } from '@/interfaces/InterfacesLocal'
import useContainerWizzardSteps from '@/state/useContainerWizzardSteps'
import { useAppSelector } from '@/store/hooks'

import { IContainer } from '../../../interfaces/interfaces'
import { columns } from './columns'

const costTypes: SelectInterface[] = [
  {
    id: 'SUPPLIER',
    name: 'Proveedor',
  },
  {
    id: 'OPERATIONAL',
    name: 'Operacional',
  },
  {
    id: 'INDIRECT',
    name: 'Indirecto',
  },
]

export default function CostsContainerTab({
  isEditing,
  editContainer,
}: {
  isEditing?: boolean
  editContainer?: IContainer
}) {
  const context = isEditing
    ? useModalWizardTabsContext()
    : useModalWizardStepsContext()
  const { useFormWizzard } = context
  const [showCostModal, setShowCostModal] = useState(false)
  const [isSending, setIsSending] = useState(false)
  const [costTable, setCostTable] = useState<ICost[]>([])
  const [isCostEditing, setIsCostEditing] = useState(false)
  const [showDeleteCost, setShowDeleteCost] = useState(false)
  const [costToDelete, setCostToDelete] = useState<number>()
  const data = useContainerWizzardSteps((state) => state.data)
  const updateData = useContainerWizzardSteps((state) => state.updateData)

  const { data: currencies } = useQuery({
    queryKey: ['currencies'],
    queryFn: getCurrencies,
  })

  const { data: costCategories } = useQuery({
    queryKey: ['costCategories'],
    queryFn: getCostCategories,
  })

  const queryClient = useQueryClient()

  const { data: containerCosts, isLoading } = useQuery({
    queryKey: ['container_costs', editContainer?.id],
    queryFn: () => getContainerCosts({ id: editContainer?.id as number }),
    enabled: Boolean(editContainer?.id),
    staleTime: 5 * 60 * 1000,
  })

  const postContainerCostsMutation = useMutation({
    mutationFn: postContainerCosts,
    onSuccess: () => {
      setIsSending(false)
      setShowCostModal(false)
      toast.success('El costo del contenedor se ha creado con éxito')
      queryClient.invalidateQueries({ queryKey: ['container_costs'] })

      form.reset()
      setIsCostEditing(false)
    },
    onError: (response: any) => {
      setIsSending(false)
      const errorMessage = getErrorMessage(response)
      toast.error(errorMessage || 'Error al crear el costo del contenedor')
    },
  })

  const updateContainerCostsMutation = useMutation({
    mutationFn: patchContainerCosts,
    onSuccess: () => {
      setIsSending(false)
      setShowCostModal(false)
      toast.success('El costo del contenedor se ha actualizado con éxito')
      queryClient.invalidateQueries({ queryKey: ['container_costs'] })

      form.reset()
      setIsCostEditing(false)
    },
    onError: (response: any) => {
      setIsSending(false)
      const errorMessage = getErrorMessage(response)
      toast.error(errorMessage || 'Error al actualizar el costo del contenedor')
    },
  })

  const deleteContainerCostsMutation = useMutation({
    mutationFn: deleteContainerCosts,
    onSuccess: () => {
      setIsSending(false)
      setShowDeleteCost(false)
      toast.success('El costo del contenedor se ha eliminado con éxito')
      queryClient.invalidateQueries({ queryKey: ['container_costs'] })

      form.reset()
      setIsCostEditing(false)
    },
    onError: (response: any) => {
      setIsSending(false)
      const errorMessage = getErrorMessage(response)
      toast.error(errorMessage || 'Error al eliminar el costo del contenedor')
    },
  })

  useEffect(() => {
    if (!isEditing) {
      const initialData = (
        Array.isArray(data.listCosts) ? data.listCosts : []
      ).map((item: any, idx: number) => ({
        id: idx,
        type: item.type || 'SUPPLIER',
        price: {
          amount: item.cost?.amount || item.costAmount || 0,
          codeCurrency:
            item.cost?.codeCurrency || item.costCodecurrency || 'USD',
        },
        // Keep the original structure for compatibility
        cost: {
          amount: item.cost?.amount || item.costAmount || 0,
          codeCurrency:
            item.cost?.codeCurrency || item.costCodecurrency || 'USD',
        },
        category: item.category || '',
        observations: item.observations || '',
        actions: {
          remove: true,
          edit: true,
        },
      }))
      setCostTable(initialData as ICost[])
    }
  }, [data.listCosts, isEditing])

  useEffect(() => {
    if (isEditing) {
      if (!isLoading && containerCosts) {
        // Check if containerCosts is directly an array or has a fixedCosts property
        const costsArray = Array.isArray(containerCosts)
          ? containerCosts
          : containerCosts.fixedCosts || []

        if (Array.isArray(costsArray) && costsArray.length > 0) {
          const initialData = costsArray.map((item: any) => ({
            id: item.id,
            type: item.type || 'SUPPLIER',
            price: {
              amount: item.price?.amount || item.costAmount || 0,
              codeCurrency: item.price?.codeCurrency || 'USD',
            },
            // Keep the original structure for compatibility
            cost: {
              amount: item.price?.amount || item.costAmount || 0,
              codeCurrency: item.price?.codeCurrency || 'USD',
            },
            category: item?.fixedCostCategory?.name || 'Sin categoría',
            fixedCostCategoryId: item?.fixedCostCategory?.id || 1, // Agregar el ID para edición
            observations: item?.observations || '-',
            actions: {
              remove: true,
              edit: true,
            },
          }))

          setCostTable(initialData as ICost[])
        } else {
          setCostTable([])
        }
      } else if (!isLoading) {
        setCostTable([])
      }
    }
  }, [containerCosts, isLoading, isEditing])

  // Exponer función de guardado para el botón "Guardar" del modal
  useEffect(() => {
    if (isEditing) {
      // @ts-ignore - Adding custom property to window for parent access
      window.handleSaveCosts = () => {
        toast.success('Costos guardados')
        // No cerrar modal automáticamente en modo edición
      }
      // @ts-ignore - Expose loading state
      window.isSavingCosts = false
    }
  }, [isEditing])

  const { business } = useAppSelector((state) => state.init)

  const form = useForm()
  const saveCost = (cost: any) => {
    const type = cost.type
    const registeredPrice = {
      amount: Number(cost.cost?.amount || 0),
      codeCurrency: cost.cost?.codeCurrency || 'USD',
    }
    const fixedCostCategoryId = cost.category || cost.fixedCostCategoryId || 1
    const observations = cost.observations || 'Sin observaciones' // Valor por defecto si está vacío
    const id = cost.id

    console.log('Saving cost with data:', {
      type,
      registeredPrice,
      fixedCostCategoryId,
      observations,
      originalCost: cost,
    })

    if (isEditing) {
      if (isCostEditing) {
        setIsSending(true)
        updateContainerCostsMutation.mutate({
          id,
          params: {
            type,
            registeredPrice,
            fixedCostCategoryId,
            observations,
          },
        })
      } else {
        setIsSending(true)
        postContainerCostsMutation.mutate({
          id: editContainer?.id as number,
          params: {
            type,
            registeredPrice,
            fixedCostCategoryId,
            observations,
          },
        })
      }
    } else {
      if (isCostEditing) {
        // Editar costo existente
        updateData({
          ...data,
          listCosts: (Array.isArray(data.listCosts) ? data.listCosts : []).map(
            (item, idx) =>
              idx === id
                ? {
                    cost: {
                      amount: Number(cost.cost?.amount || 0),
                      codeCurrency: cost.cost?.codeCurrency || 'USD',
                    },
                    type: cost.type || 'UNDEFINED',
                    fixedCostCategoryId:
                      cost.category || cost.fixedCostCategoryId || 1,
                    observations: cost.observations || 'Sin observaciones',
                  }
                : item
          ),
        })
        setCostTable((prev) =>
          prev.map((item) =>
            item.id === id
              ? {
                  ...item,
                  type: cost.type || 'SUPPLIER',
                  price: {
                    amount: Number(cost.cost?.amount || 0),
                    codeCurrency: cost.cost?.codeCurrency || 'USD',
                  },
                  cost: {
                    amount: Number(cost.cost?.amount || 0),
                    codeCurrency: cost.cost?.codeCurrency || 'USD',
                  },
                  observations: cost.observations || 'Sin observaciones',
                }
              : item
          )
        )
        toast.success('El costo del contenedor se ha editado con éxito')
      } else {
        // Añadir nuevo costo
        updateData({
          ...data,
          listCosts: [
            ...(Array.isArray(data.listCosts) ? data.listCosts : []),
            {
              cost: {
                amount: Number(cost.cost?.amount || 0),
                codeCurrency: cost.cost?.codeCurrency || 'USD',
              },
              type: cost.type || 'UNDEFINED',
              fixedCostCategoryId:
                cost.category || cost.fixedCostCategoryId || 1,
              observations: cost.observations || 'Sin observaciones',
            },
          ],
        })
        setCostTable((prev) => [
          ...prev,
          {
            id: prev.length,
            type: cost.type || 'SUPPLIER',
            price: {
              amount: Number(cost.cost?.amount || 0),
              codeCurrency: cost.cost?.codeCurrency || 'USD',
            },
            cost: {
              amount: Number(cost.cost?.amount || 0),
              codeCurrency: cost.cost?.codeCurrency || 'USD',
            },
            observations: cost.observations || 'Sin observaciones',
            actions: {
              remove: true,
              edit: true,
            },
          },
        ])
        toast.success('El costo del contenedor se ha añadido con éxito')
      }
      form.reset()
      setShowCostModal(false)
      setIsCostEditing(false)
    }
  }

  return (
    <div className={'h-[60dvh]'}>
      {isLoading ? (
        <SpinnerLoading />
      ) : (
        <TableContextProvider
          data={costTable.filter(
            (item) =>
              item &&
              typeof item.cost?.amount === 'number' &&
              item.cost?.codeCurrency
          )}
          isLoading={false}
          leftButtons={[
            {
              type: 'text',
              label: 'Listado de costos',
            },
          ]}
          rightButtons={[
            {
              type: 'button',
              icon: <PlusIcon className="h-5 w-5 text-black" strokeWidth={2} />,
              label: 'Añadir costo',
              onClick: () => setShowCostModal(true),
            },
          ]}
        >
          <DataTable
            columns={[
              ...columns,
              {
                accessorKey: 'actions',
                header: 'Acciones',
                cell: ({ row }) => {
                  return (
                    <div className={'flex justify-center'}>
                      {row.original.actions.edit && (
                        <div
                          className={`p-2`}
                          onClick={() => {
                            form.setValue('id', row.original.id)
                            // Use registeredPrice data first (from backend), fallback to price/cost for compatibility
                            const amount =
                              row.original.registeredPrice?.amount ||
                              row.original.price?.amount ||
                              row.original.cost?.amount ||
                              0
                            const codeCurrency =
                              row.original.registeredPrice?.codeCurrency ||
                              row.original.price?.codeCurrency ||
                              row.original.cost?.codeCurrency ||
                              'USD'

                            form.setValue('cost', {
                              amount: amount,
                              codeCurrency: codeCurrency,
                            })
                            form.setValue(
                              'type',
                              row.original.type || 'SUPPLIER'
                            )
                            form.setValue(
                              'category',
                              row.original.fixedCostCategoryId || 1
                            )
                            form.setValue(
                              'observations',
                              row.original.observations || ''
                            )
                            setShowCostModal(true)
                            setIsCostEditing(true)
                          }}
                          role={'button'}
                        >
                          <PencilSquareIcon
                            className={`h-5 w-5 text-blue-700`}
                          />
                        </div>
                      )}
                      <div
                        className={`p-2`}
                        onClick={() => {
                          setShowDeleteCost(true)
                          setCostToDelete(row.original.id)
                        }}
                        role={'button'}
                      >
                        <TrashIcon className={`h-5 w-5 text-red-700`} />
                      </div>
                    </div>
                  )
                },
              },
            ]}
            storageKey={'DocumentsExpTab'}
          />
        </TableContextProvider>
      )}
      {showCostModal && (
        <Modal
          isOpen={showCostModal}
          onClose={() => {
            setIsCostEditing(false)
            setShowCostModal(false)
            form.reset()
          }}
          showDialogAlert={false}
          size={'xs'}
          title={isCostEditing ? 'Editar costo' : 'Añadir costo'}
        >
          <div className={'flex w-full flex-col'}>
            <Form {...form}>
              <div className={'flex w-full flex-col space-y-2'}>
                {costCategories && (
                  <SelectForm
                    control={form.control}
                    data={costCategories?.map(({ id, name }) => ({
                      id,
                      title: name,
                    }))}
                    label={'Categoría de costo'}
                    name={'category'}
                    rules={{
                      required: 'Campo requerido',
                    }}
                  />
                )}
                {currencies && (
                  <InputCurrencyForm
                    control={form.control}
                    currenciesData={currencies?.map(({ code }) => ({
                      code: code,
                      displayName: code,
                    }))}
                    label={'Costo'}
                    name={'cost'}
                    rules={{
                      required: 'Campo requerido',
                      validate: (value: {
                        amount?: number
                        codeCurrency: string
                      }) => {
                        if (value?.amount !== undefined && value.amount > 0) {
                          const amountStr = value.amount.toString()
                          const decimalPart = amountStr.split('.')[1]
                          if (decimalPart && decimalPart.length > 10) {
                            return 'El monto no puede tener más de 10 decimales'
                          }
                        }
                        return true
                      },
                    }}
                  />
                )}
                <SelectForm
                  control={form.control}
                  data={[
                    { id: 'SUPPLIER', title: 'Proveedor' },
                    { id: 'OPERATIONAL', title: 'Operacional' },
                    { id: 'INDIRECT', title: 'Indirecto' },
                    { id: 'UNDEFINED', title: 'Indefinido' },
                  ]}
                  label={'Tipo de costo'}
                  name={'type'}
                  rules={{ required: 'Campo requerido' }}
                />
              </div>
              <div className={'mt-10 flex w-full justify-between'}>
                <ButtonCancelar
                  disabled={isSending}
                  onClick={() => {
                    setIsCostEditing(false)
                    setShowCostModal(false)
                    form.reset()
                  }}
                  variant="outline"
                >
                  Cancelar
                </ButtonCancelar>
                {isSending ? (
                  <ButtonLoading
                    loading={isSending}
                    name="Guardando..."
                  ></ButtonLoading>
                ) : (
                  <ButtonPrimary
                    onClick={form.handleSubmit(saveCost)}
                    text={'Guardar'}
                  />
                )}
              </div>
            </Form>
          </div>
        </Modal>
      )}
      <AlertDialog open={showDeleteCost}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar el costo?</AlertDialogTitle>
            <AlertDialogDescription>
              Si elimina ahora, perderá todos los datos asociados a este costo.
              ¿Está seguro de que desea eliminar?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setShowDeleteCost(false)}>
              Cancelar
            </AlertDialogCancel>
            <Button
              action={() => {
                if (isEditing) {
                  setIsSending(true)
                  deleteContainerCostsMutation.mutate(costToDelete as number)
                } else {
                  setCostTable((prev) =>
                    prev.filter((item) => item.id !== costToDelete)
                  )
                  updateData({
                    ...data,
                    listCosts: data.listCosts.filter(
                      (_, idx) => idx !== costToDelete
                    ),
                  })
                  setShowDeleteCost(false)
                  toast.success(
                    'El costo del contenedor se ha eliminado con éxito'
                  )
                }
              }}
              disabled={isSending}
              loading={isSending}
              name={'Sí, eliminar'}
            />
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
